/**
 * Membership Linked Product Auto-Add (Optimized)
 * Automatically adds/removes a linked product when membership is added/removed from cart
 * Enforces quantity of 1 for the welcome gift
 */

class MembershipLinkedProduct {
  constructor(config) {
    // Configuration
    this.membershipProductId = config.membershipProductId;
    this.membershipHandle = config.membershipHandle || 'membership';
    this.membershipVariantId = config.membershipVariantId;
    this.membershipSellingPlanId = config.membershipSellingPlanId;
    this.membershipItemVariantId = config.membershipItemVariantId;
    this.membershipItemProductId = config.membershipItemProductId;
    this.linkedVariantId = config.linkedVariantId;
    this.linkedProductId = config.linkedProductId;
    
    // All membership mutations run through one queue. Cart events can arrive
    // late or out of order, so they are signals to read the cart again rather
    // than authoritative snapshots for a mutation.
    this.mutationQueue = Promise.resolve();
    this.latestCart = null;
    this.pendingCartFingerprint = '';
    this.activeCartFingerprint = '';
    this.fetchPromise = null;
    this.reconcileTimer = null;
    this.normalizingCart = false;
    this.reconcilePromise = null;
    this.reconcileRequested = false;
    this.membershipActionPromise = null;
    
    // Property to mark linked items in cart
    this.LINKED_PROPERTY_KEY = '_membership_gift';
    this.LINKED_PROPERTY_VALUE = 'true';
    this.GIFTED_MEMBER_PROPERTY_KEY = '_gifted_member_welcome_gift';
    this.GIFTED_MEMBER_PROPERTY_VALUE = 'true';
    this.GIFTED_MEMBER_CLAIM_TOKEN_KEY = '_gifted_member_claim_token';
    
    this.init();
  }

  init() {
    // Initial check immediately
    this.checkCart();
    
    // Listen for theme's cart update events (PRIMARY event source)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Skip only our own updates to prevent loops
        if (event.source === 'membership-linked') return;
        this.scheduleCheck(event.cartData);
      });
    }

    document.addEventListener('cart:updated', (event) => {
      if (event.detail?.source === 'membership-linked') return;
      this.scheduleCheck(event.detail?.cartData || null);
    });

    document.addEventListener('cart-drawer:opened', (event) => {
      this.scheduleCheck(event.detail?.cartData || null, 80);
    });

  }

  hasFullCartSnapshot(cart) {
    return Boolean(cart && Array.isArray(cart.items) && typeof cart.item_count !== 'undefined');
  }

  setCart(cart) {
    if (!this.hasFullCartSnapshot(cart)) return cart;
    this.latestCart = cart;
    window.cartState?.setCart(cart);
    return cart;
  }

  getCartFingerprint(cart) {
    if (!this.hasFullCartSnapshot(cart)) return '';

    return cart.items
      .map((item) => [
        item.key,
        item.quantity,
        item.variant_id,
        item.selling_plan_allocation?.selling_plan?.id || ''
      ].join(':'))
      .sort()
      .join('|');
  }

  scheduleCheck(cart, delay = 0) {
    if (this.hasFullCartSnapshot(cart)) {
      const fingerprint = this.getCartFingerprint(cart);
      if (
        fingerprint
        && (fingerprint === this.pendingCartFingerprint || fingerprint === this.activeCartFingerprint)
      ) {
        return;
      }

      this.pendingCartFingerprint = fingerprint;
    }

    clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => {
      this.checkCart();
    }, delay);
  }

  enqueueMutation(operation) {
    const queuedOperation = this.mutationQueue.then(operation, operation);
    this.mutationQueue = queuedOperation.catch(() => null);
    return queuedOperation;
  }

  async checkCart(cartOverride) {
    if (this.hasFullCartSnapshot(cartOverride)) {
      this.pendingCartFingerprint = this.getCartFingerprint(cartOverride);
    }

    this.reconcileRequested = true;
    if (this.reconcilePromise) return this.reconcilePromise;

    this.reconcilePromise = this.enqueueMutation(async () => {
      try {
        let cart = null;
        let passes = 0;

        do {
          this.reconcileRequested = false;
          this.activeCartFingerprint = this.pendingCartFingerprint;
          this.pendingCartFingerprint = '';

          try {
            cart = await this.reconcileFreshCart();
          } finally {
            this.activeCartFingerprint = '';
          }
          passes += 1;
        } while (this.reconcileRequested && passes < 4);

        return cart;
      } catch (error) {
        console.error('[Membership Linked] Check failed:', error);
      }
    }).finally(() => {
      this.reconcilePromise = null;
      if (this.reconcileRequested) this.scheduleCheck(null, 0);
    });

    return this.reconcilePromise;
  }

  async reconcileFreshCart() {
    // Cart events are only reconciliation signals. Always mutate from a fresh
    // server snapshot because theme/app events can arrive late or out of order.
    let cart = await this.fetchCart(true);
    const initialFingerprint = this.getCartFingerprint(cart);

    cart = await this.reconcileMembershipProduct(cart) || cart;

    const hasMembership = this.cartHasMembership(cart);
    cart = await this.normalizeWelcomeGift(cart, { hasMembership }) || cart;

    const giftedMemberCleanup = this.getGiftedMemberCleanup(cart);
    if (giftedMemberCleanup) {
      cart = await this.applyGiftedMemberCleanup(giftedMemberCleanup) || cart;
    }

    if (hasMembership && !this.getLinkedProduct(cart)) {
      cart = await this.addLinkedProductOnce(cart) || cart;
    }

    this.setCart(cart);
    this.updateMembershipPurchaseControls(cart);

    if (this.getCartFingerprint(cart) !== initialFingerprint) {
      this.triggerCartUpdate(cart);
    }

    return cart;
  }

  cartHasMembership(cart) {
    return cart.items.some((item) => this.isMembershipSubscription(item) || this.isMembershipItem(item));
  }

  isMembershipSubscription(item) {
    if (!item || item.properties?.[this.GIFTED_MEMBER_PROPERTY_KEY] === this.GIFTED_MEMBER_PROPERTY_VALUE) return false;

    // The normal membership product is incompatible with Purple Dot preorders
    // regardless of whether another app preserves its selling-plan allocation.
    return item.product_id?.toString() === this.membershipProductId.toString();
  }

  isMembershipItem(item) {
    if (!item || item.properties?.[this.GIFTED_MEMBER_PROPERTY_KEY] === this.GIFTED_MEMBER_PROPERTY_VALUE) return false;

    return Boolean(
      this.membershipItemVariantId
      && item.variant_id?.toString() === this.membershipItemVariantId.toString()
      && !item.selling_plan_allocation
    );
  }

  isPreorderItem(item) {
    if (!item || item.properties?.[this.LINKED_PROPERTY_KEY] === this.LINKED_PROPERTY_VALUE) return false;

    return Object.entries(item.properties || {}).some(([key, value]) => {
      const normalizedKey = String(key).toLowerCase();
      const normalizedValue = String(value || '').toLowerCase();
      return normalizedKey.includes('purple dot')
        || normalizedKey.includes('pre-order')
        || normalizedKey.includes('preorder')
        || normalizedValue.includes('purple dot')
        || normalizedValue.includes('pre-order')
        || normalizedValue.includes('preorder');
    });
  }

  cartHasPreorder(cart) {
    return Boolean(cart?.items?.some((item) => this.isPreorderItem(item)));
  }

  shouldBlockMembershipPurchase(cart) {
    return this.cartHasPreorder(cart)
      && cart.items.some((item) => this.isMembershipItem(item));
  }

  updateMembershipPurchaseControls(cart) {
    const shouldBlock = this.shouldBlockMembershipPurchase(cart);
    const controls = document.querySelectorAll([
      '[data-membership-upsell-checkbox]',
      '#membershipCheckbox',
      '.membership-promo__cta',
      `product-form[data-product-handle="${this.membershipHandle}"] button[type="submit"]`
    ].join(','));

    controls.forEach((control) => {
      control.disabled = shouldBlock;
      control.setAttribute('aria-disabled', String(shouldBlock));
      if (shouldBlock) {
        control.title = 'Membership is already in your cart.';
      } else if (control.title === 'Membership is already in your cart.') {
        control.removeAttribute('title');
      }
    });
  }

  hasIncompatibleMembershipPreorder(cart) {
    return this.cartHasPreorder(cart)
      && cart.items.some((item) => this.isMembershipSubscription(item));
  }

  async reconcileMembershipProduct(cart) {
    if (!this.membershipItemVariantId || !this.hasFullCartSnapshot(cart)) return cart;

    const subscriptionItems = cart.items.filter((item) => this.isMembershipSubscription(item));
    const membershipItems = cart.items.filter((item) => this.isMembershipItem(item));
    if (!subscriptionItems.length && !membershipItems.length) return cart;

    const shouldUseMembershipItem = this.cartHasPreorder(cart);

    const targetItems = shouldUseMembershipItem ? membershipItems : subscriptionItems;
    const obsoleteItems = shouldUseMembershipItem ? subscriptionItems : membershipItems;

    if (obsoleteItems.length && !targetItems.length) {
      return this.withMembershipUpdate(() => (
        this.replaceMembershipProduct(cart, obsoleteItems)
      ));
    }

    if (targetItems.length > 1 || targetItems.some((item) => item.quantity > 1) || obsoleteItems.length) {
      const normalize = () => this.normalizeMembershipLines(targetItems, obsoleteItems);
      return obsoleteItems.length ? this.withMembershipUpdate(normalize) : normalize();
    }

    return cart;
  }

  async withMembershipUpdate(operation) {
    document.dispatchEvent(new CustomEvent('membership:updating'));
    try {
      // The authoritative membership update event owns the drawer render.
      // Rendering here as well caused duplicate section fetches and flicker.
      return await operation();
    } finally {
      document.dispatchEvent(new CustomEvent('membership:updated'));
    }
  }

  async normalizeMembershipLines(targetItems, obsoleteItems = []) {
    const updates = {};
    const keptItem = targetItems[0];
    if (keptItem && keptItem.quantity !== 1) updates[keptItem.key] = 1;
    targetItems.slice(1).forEach((item) => { updates[item.key] = 0; });
    obsoleteItems.forEach((item) => { updates[item.key] = 0; });

    if (!Object.keys(updates).length) return this.latestCart;

    const updatedCart = await this.updateCartItems(updates);
    return updatedCart;
  }

  async replaceMembershipProduct(cart, currentItems) {
    const shouldUseMembershipItem = this.cartHasPreorder(cart);
    const items = [{
      id: shouldUseMembershipItem ? this.membershipItemVariantId : this.membershipVariantId,
      quantity: 1,
      ...(shouldUseMembershipItem ? {} : { selling_plan: this.membershipSellingPlanId })
    }];

    if (!this.getLinkedProduct(cart)) {
      items.push({
        id: this.linkedVariantId,
        quantity: 1,
        properties: {
          [this.LINKED_PROPERTY_KEY]: this.LINKED_PROPERTY_VALUE,
          '_linked_to_product': this.membershipProductId
        }
      });
    }

    // Add the replacement behind the loading state, then remove the obsolete
    // line in one cart update. The final update response is a full cart with
    // final drawer sections, which avoids a cart read and a section refresh.
    await this.addCartItems(items);

    const removals = {};
    currentItems.forEach((item) => { removals[item.key] = 0; });
    let freshCart = await this.updateCartItems(removals);

    // A late cart writer can still merge into the replacement line while the
    // requests are in flight. Normalize the authoritative update response once
    // before exposing the drawer.
    const finalShouldUseMembershipItem = this.cartHasPreorder(freshCart);
    const subscriptionItems = freshCart.items.filter((item) => this.isMembershipSubscription(item));
    const membershipItems = freshCart.items.filter((item) => this.isMembershipItem(item));
    const targetItems = finalShouldUseMembershipItem ? membershipItems : subscriptionItems;
    const obsoleteItems = finalShouldUseMembershipItem ? subscriptionItems : membershipItems;

    if (targetItems.length) {
      if (targetItems.length > 1 || targetItems.some((item) => item.quantity !== 1) || obsoleteItems.length) {
        freshCart = await this.normalizeMembershipLines(targetItems, obsoleteItems);
      }
      return freshCart;
    }

    // If preorder state changed during the two-request swap, immediately run
    // another serialized pass against the returned cart.
    this.setCart(freshCart);
    this.reconcileRequested = true;
    return freshCart;
  }

  getLinkedProduct(cart) {
    return cart.items.find(item => {
      const matchesId = this.isWelcomeGiftItem(item);
      const isMarked = item.properties?.[this.LINKED_PROPERTY_KEY] === this.LINKED_PROPERTY_VALUE;
      return matchesId && isMarked;
    }) || null;
  }

  getWelcomeGiftItems(cart) {
    if (!this.hasFullCartSnapshot(cart)) return [];

    return cart.items.filter((item) => this.isWelcomeGiftItem(item));
  }

  isWelcomeGiftItem(item) {
    if (!item) return false;

    const isGiftedMemberClaim = (
      item.properties?.[this.GIFTED_MEMBER_PROPERTY_KEY] === this.GIFTED_MEMBER_PROPERTY_VALUE
    );
    if (isGiftedMemberClaim) return false;

    return (
      item.variant_id?.toString() === this.linkedVariantId.toString() ||
      item.product_id?.toString() === this.linkedProductId.toString() ||
      item.properties?.[this.LINKED_PROPERTY_KEY] === this.LINKED_PROPERTY_VALUE
    );
  }

  getWelcomeGiftCleanup(cart, options = {}) {
    const hasMembership = typeof options.hasMembership === 'boolean'
      ? options.hasMembership
      : this.cartHasMembership(cart);
    const giftItems = this.getWelcomeGiftItems(cart);

    if (!giftItems.length) return null;

    const updates = {};
    let keptGift = null;

    giftItems.forEach((item) => {
      const isMarkedGift = item.properties?.[this.LINKED_PROPERTY_KEY] === this.LINKED_PROPERTY_VALUE;

      if (!hasMembership) {
        updates[item.key] = 0;
        return;
      }

      if (!keptGift && isMarkedGift) {
        keptGift = item;
        if (item.quantity !== 1) {
          updates[item.key] = 1;
        }
        return;
      }

      updates[item.key] = 0;
    });

    if (!hasMembership || keptGift || Object.keys(updates).length) {
      return Object.keys(updates).length ? updates : null;
    }

    return null;
  }

  async normalizeWelcomeGift(cartOverride, options = {}) {
    if (this.normalizingCart) {
      return cartOverride;
    }

    const cart = this.hasFullCartSnapshot(cartOverride) ? cartOverride : await this.fetchCart();
    const updates = this.getWelcomeGiftCleanup(cart, options);

    if (!updates) {
      return cart;
    }

    this.normalizingCart = true;

    try {
      const updatedCart = await this.updateCartItems(updates);
      return updatedCart;
    } finally {
      this.normalizingCart = false;
    }
  }

  getGiftedMemberCleanup(cart) {
    const giftedItems = cart.items.filter((item) =>
      item.properties?.[this.GIFTED_MEMBER_PROPERTY_KEY] === this.GIFTED_MEMBER_PROPERTY_VALUE
    );

    if (!giftedItems.length) {
      return null;
    }

    const updates = {};
    let keptValidItem = null;

    giftedItems.forEach((item) => {
      const claimToken = String(item.properties?.[this.GIFTED_MEMBER_CLAIM_TOKEN_KEY] || '').trim();
      const hasClaimToken = Boolean(claimToken);

      if (!hasClaimToken) {
        updates[item.key] = 0;
        return;
      }

      if (!keptValidItem) {
        keptValidItem = item;
        if (item.quantity > 1) {
          updates[item.key] = 1;
        }
        return;
      }

      updates[item.key] = 0;
    });

    return Object.keys(updates).length ? updates : null;
  }

  async updateCartItems(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update membership cart lines');
    }

    const updatedCart = await response.json();
    this.setCart(updatedCart);
    return updatedCart;
  }

  async addCartItems(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      let message = 'Failed to add membership cart lines';
      try {
        const errorData = await response.json();
        message = errorData.description || errorData.message || message;
      } catch (_error) {
        // Keep the customer-safe fallback message when Shopify returns HTML.
      }
      throw new Error(message);
    }

    return response.json();
  }

  async addLinkedProductOnce(cartOverride) {
    try {
      let cart = this.hasFullCartSnapshot(cartOverride)
        ? cartOverride
        : await this.fetchCart();
      const hasMembership = this.cartHasMembership(cart);

      if (!hasMembership) {
        return cart;
      }

      if (this.getLinkedProduct(cart)) {
        return cart;
      }

      await this.addCartItems([{
          id: this.linkedVariantId,
          quantity: 1,
          properties: {
            [this.LINKED_PROPERTY_KEY]: this.LINKED_PROPERTY_VALUE,
            '_linked_to_product': this.membershipProductId
          }
        }]);

      const updatedCart = await this.fetchCart(true);
      return updatedCart;
      
    } catch (error) {
      console.error('[Membership Linked] Add failed:', error);
      return null;
    }
  }

  async addMembership(cartOverride) {
    if (this.membershipActionPromise) return this.membershipActionPromise;

    this.membershipActionPromise = this.enqueueMutation(async () => {
      // The click-time snapshot can be older than a concurrent cart mutation.
      // Start every membership action from the server cart inside the queue.
      let cart = await this.fetchCart(true);

      if (this.cartHasMembership(cart)) {
        cart = await this.reconcileMembershipProduct(cart) || cart;
      } else {
        const useMembershipItem = this.cartHasPreorder(cart);
        const items = [{
          id: useMembershipItem ? this.membershipItemVariantId : this.membershipVariantId,
          quantity: 1,
          ...(useMembershipItem ? {} : { selling_plan: this.membershipSellingPlanId })
        }];

        if (!this.getLinkedProduct(cart)) {
          items.push({
            id: this.linkedVariantId,
            quantity: 1,
            properties: {
              [this.LINKED_PROPERTY_KEY]: this.LINKED_PROPERTY_VALUE,
              '_linked_to_product': this.membershipProductId
            }
          });
        }

        await this.addCartItems(items);
        cart = await this.fetchCart(true);
        cart = await this.reconcileMembershipProduct(cart) || cart;
      }

      cart = await this.normalizeWelcomeGift(cart, {
        hasMembership: this.cartHasMembership(cart)
      }) || cart;

      this.setCart(cart);
      this.updateMembershipPurchaseControls(cart);
      // The caller renders the final drawer exactly once after this action.
      return cart;
    }).finally(() => {
      this.membershipActionPromise = null;
    });

    return this.membershipActionPromise;
  }

  async removeMembership(cartOverride) {
    if (this.membershipActionPromise) return this.membershipActionPromise;

    this.membershipActionPromise = this.enqueueMutation(async () => {
      const cart = await this.fetchCart(true);
      const updates = {};

      cart.items.forEach((item) => {
        if (this.isMembershipSubscription(item) || this.isMembershipItem(item) || this.isWelcomeGiftItem(item)) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return cart;

      const updatedCart = await this.updateCartItems(updates);
      this.updateMembershipPurchaseControls(updatedCart);
      this.triggerCartUpdate(updatedCart);
      return updatedCart;
    }).finally(() => {
      this.membershipActionPromise = null;
    });

    return this.membershipActionPromise;
  }

  async prepareCartForCheckout() {
    await this.checkCart();
    let cart = await this.fetchCart(true);

    // Checkout is the hard safety boundary. If a late cart writer restored an
    // incompatible recurring membership, reconcile that fresh snapshot once
    // before deciding whether checkout must remain blocked.
    if (this.hasIncompatibleMembershipPreorder(cart)) {
      await this.checkCart(cart);
      cart = await this.fetchCart(true);
    }

    this.updateMembershipPurchaseControls(cart);
    if (this.hasIncompatibleMembershipPreorder(cart)) {
      throw new Error('Recurring membership cannot be purchased with a preorder.');
    }
    return cart;
  }

  async renderCartDrawerAfterMembershipAdd(triggerId, cartOverride) {
    const cart = this.hasFullCartSnapshot(cartOverride)
      ? cartOverride
      : await this.prepareCartForCheckout();
    let sectionData = cart.sections;

    if (!sectionData?.['cart-drawer'] || !sectionData?.['cart-icon-bubble']) {
      const root = window.Shopify?.routes?.root || '/';
      const sections = 'cart-drawer,cart-icon-bubble';
      const response = await fetch(`${root}?sections=${sections}`);

      if (!response.ok) {
        throw new Error('Failed to refresh membership cart drawer');
      }

      sectionData = await response.json();
    }

    const renderedCart = {
      ...cart,
      sections: sectionData
    };
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents({
        sections: sectionData,
        id: triggerId
      });
    }

    return renderedCart;
  }

  async applyGiftedMemberCleanup(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      body: JSON.stringify({
        updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
      });

      if (!response.ok) {
        throw new Error('Failed to clean up gifted member welcome gifts');
      }

      const updatedCart = await response.json();
      return updatedCart;
    } catch (error) {
      console.error('[Membership Linked] Gifted member cleanup failed:', error);
    }
  }

  triggerCartUpdate(cart, source = 'membership-linked') {
    this.setCart(cart);

    // Trigger the theme's cart update via pub/sub if available
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        cartData: cart
      });
    }
    
    // Also dispatch cart:updated for other listeners
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        source,
        cartData: cart
      }
    }));

  }

  async fetchCart(forceFresh = false) {
    if (window.cartState?.getCart) {
      const cart = await window.cartState.getCart(forceFresh);
      return this.setCart(cart);
    }

    if (!forceFresh && this.fetchPromise) {
      return this.fetchPromise;
    }

    const request = fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }

        return response.json();
      })
      .then((cart) => {
        this.setCart(cart);
        return cart;
      });

    if (forceFresh) return request;

    this.fetchPromise = request.finally(() => {
      this.fetchPromise = null;
    });

    return this.fetchPromise;
  }
}

function initializeMembershipLinkedProduct() {
  // Configuration
  const config = {
    membershipProductId: '8384817987780',
    membershipHandle: window.EG_CART_DRAWER_MEMBERSHIP?.membershipHandle || 'membership',
    membershipSellingPlanId: '3513909444',
    membershipVariantId: window.EG_CART_DRAWER_MEMBERSHIP?.membershipVariantId || 45267979075780,
    membershipItemVariantId: window.EG_CART_DRAWER_MEMBERSHIP?.membershipItemVariantId || 46551139614916,
    membershipItemProductId: window.EG_CART_DRAWER_MEMBERSHIP?.membershipItemProductId || 8689890033860,
    linkedVariantId: '45637017010372',
    linkedProductId: '8467446661316'
  };

  // Initialize the system
  if (!window.membershipLinkedProduct) {
    window.membershipLinkedProduct = new MembershipLinkedProduct(config);
  }
  window.EGNormalizeMembershipWelcomeGift = () => window.membershipLinkedProduct.prepareCartForCheckout();
  window.EGRenderMembershipCartAfterAdd = (triggerId) => window.membershipLinkedProduct.renderCartDrawerAfterMembershipAdd(triggerId);
  window.EGAddMembershipToCart = async (triggerId) => {
    const cart = await window.membershipLinkedProduct.addMembership();
    const renderedCart = await window.membershipLinkedProduct.renderCartDrawerAfterMembershipAdd(triggerId, cart);
    // Notify non-rendering listeners after the drawer is final. CartItems ignores
    // this source, so it cannot start a second section render.
    window.membershipLinkedProduct.triggerCartUpdate(renderedCart, 'membership-checkbox');
    return renderedCart;
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMembershipLinkedProduct, { once: true });
} else {
  initializeMembershipLinkedProduct();
}

(function() {
  if (window.__EG_MEMBERSHIP_CHECKOUT_GUARD_BOUND) return;
  window.__EG_MEMBERSHIP_CHECKOUT_GUARD_BOUND = true;

  let checkoutGuardRunning = false;

  function isCheckoutControl(element) {
    return Boolean(
      element?.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
    );
  }

  function getCheckoutControl(element) {
    return element?.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
  }

  function ensureCheckoutInput(form) {
    if (!form || form.querySelector('input[name="checkout"]')) return;

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    form.appendChild(checkoutInput);
  }

  async function runCheckoutGuard(event, control) {
    if (checkoutGuardRunning || !window.EGNormalizeMembershipWelcomeGift) {
      return false;
    }

    checkoutGuardRunning = true;
    event.preventDefault();
    event.stopImmediatePropagation();

    const originalDisabled = control?.disabled;
    if (control && 'disabled' in control) {
      control.disabled = true;
    }

    try {
      await window.EGNormalizeMembershipWelcomeGift();

      if (control?.tagName === 'A') {
        window.location.href = control.href;
        return true;
      }

      const formId = control?.getAttribute('form');
      const form = control?.closest('form') || (formId ? document.getElementById(formId) : null);

      if (form) {
        ensureCheckoutInput(form);
        form.submit();
        return true;
      }
    } catch (error) {
      console.error('[Membership Linked] Checkout guard failed:', error);
      if (error.message === 'Recurring membership cannot be purchased with a preorder.') {
        window.alert('Please wait while we update your membership for this preorder, then try checkout again.');
      }
      checkoutGuardRunning = false;

      if (control && 'disabled' in control) {
        control.disabled = originalDisabled;
      }
    }

    return false;
  }

  document.addEventListener('click', (event) => {
    if (!isCheckoutControl(event.target)) return;

    const control = getCheckoutControl(event.target);
    runCheckoutGuard(event, control);
  }, true);

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!submitter || !isCheckoutControl(submitter)) return;

    runCheckoutGuard(event, submitter);
  }, true);
})();
